import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import * as api from '../../services/integration-api.js';

import { useConnectorScope } from './connector-scope.js';

/**
 * Dynamics 365 connects with a **service principal**, not OAuth: an Entra app registration the
 * customer has also added to their Dataverse as an application user. There is no consent popup —
 * interactive sign-in is blocked outright in some customer tenants by Conditional Access, which is
 * part of why this is the only flow offered.
 *
 * Four fields, and the environment URL matters most: it is stored on the connection rather than
 * configured per deployment, so one workspace can point at Dev while another points at Test or a
 * different customer entirely.
 */

interface FormErrors {
  environmentUrl?: string;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
  submit?: string;
}

/**
 * Client-side mirror of identity's `environment_url` allowlist. Two things it must get right, both
 * learned from a real org (`hei-d365-husa-d-us-1.crm.dynamics.com`): the org name can contain
 * hyphens and digits, and the region segment is EMPTY for North America (`.crm.`, no number). A
 * stricter pattern rejects a perfectly good URL and the user has no way to tell why.
 *
 * The server re-validates and its message is surfaced verbatim on submit — this only exists to fail
 * fast before a round-trip.
 */
const DATAVERSE_HOST =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.api)?\.crm[0-9]*\.dynamics\.com$/;

function isDataverseUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === 'https:' &&
      // A port or userinfo is not part of a Dataverse host. The server strips both when it
      // canonicalizes, so accepting them here would show the user a value that is not what gets
      // stored.
      parsed.port === '' &&
      parsed.username === '' &&
      parsed.password === '' &&
      DATAVERSE_HOST.test(parsed.hostname)
    );
  } catch {
    return false;
  }
}

/**
 * The origin alone, matching what identity stores. People copy the URL out of the browser, so a
 * path and query is the common case — and `.api.crm` is what Power Platform's Developer Resources
 * page shows. Canonicalizing here means the value the user sees accepted is the value that ends up
 * on the connection, rather than something the server quietly rewrote.
 */
function toEnvironmentOrigin(value: string): string {
  try {
    return `https://${new URL(value).hostname.replace(/\.api\.crm/, '.crm')}`;
  } catch {
    return value;
  }
}

function validate(values: api.Dynamics365CredentialsPayload): FormErrors {
  const errors: FormErrors = {};
  if (!values.environmentUrl) {
    errors.environmentUrl = 'Environment URL is required';
  } else if (!isDataverseUrl(values.environmentUrl)) {
    errors.environmentUrl =
      'Enter the environment URL, e.g. https://contoso.crm4.dynamics.com';
  }
  if (!values.tenantId) errors.tenantId = 'Directory (tenant) ID is required';
  if (!values.clientId) errors.clientId = 'Application (client) ID is required';
  if (!values.clientSecret) {
    errors.clientSecret = 'Client secret is required';
  } else if (values.clientSecret.length < 8) {
    errors.clientSecret = 'Client secret looks too short';
  }
  return errors;
}

type TextField = 'environmentUrl' | 'tenantId' | 'clientId';

export function Dynamics365CredentialsForm({
  onSaved,
  onCancel,
  details,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
}): JSX.Element {
  const [values, setValues] = useState<api.Dynamics365CredentialsPayload>(
    () => ({
      tenantId: '',
      clientId: '',
      clientSecret: '',
      environmentUrl: '',
    }),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();

  const handleChange =
    (field: TextField | 'clientSecret') =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = e.target;
      setValues((v) => ({ ...v, [field]: value }));
      if (errors[field]) setErrors((es) => ({ ...es, [field]: undefined }));
    };

  /** Reads the clipboard into the secret field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setValues((v) => ({ ...v, clientSecret: text.trim() }));
      if (errors.clientSecret)
        setErrors((es) => ({ ...es, clientSecret: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const trimmed: api.Dynamics365CredentialsPayload = {
      environmentUrl: values.environmentUrl.trim(),
      tenantId: values.tenantId.trim(),
      clientId: values.clientId.trim(),
      clientSecret: values.clientSecret.trim(),
    };
    const found = validate(trimmed);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    /**
     * Canonicalize AFTER validating, never before. `toEnvironmentOrigin` rebuilds the URL as
     * `https://<host>`, so running it first would silently upgrade an `http://` value and defeat the
     * protocol check the validator just made.
     */
    trimmed.environmentUrl = toEnvironmentOrigin(trimmed.environmentUrl);
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveDynamics365Credentials(trimmed, workspaceId, details);
      onSaved();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      {/*
        Two things a first-time connector cannot guess, and both cause a dead end without them: this
        connection is theirs and reaches nobody else, so each person connects their own; and the
        credential is not something they can create themselves — a Dynamics administrator has to
        register the app AND add it inside Dataverse, which is the step people miss.
      */}
      <p className="text-xs text-muted-foreground">
        This connection is yours — nobody else here uses it. You cannot create
        the credential yourself, so ask a Dynamics 365 administrator for one:
        they register an app in Microsoft Entra, then add it in Dynamics under{' '}
        <span className="font-medium">
          Settings → Security → Users → Application users
        </span>{' '}
        and give it a security role.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="d365-environment-url" className="text-xs">
          Environment URL
        </Label>
        <Input
          id="d365-environment-url"
          type="url"
          autoComplete="url"
          spellCheck={false}
          placeholder="https://contoso.crm4.dynamics.com"
          value={values.environmentUrl}
          onChange={handleChange('environmentUrl')}
          disabled={submitting}
          aria-invalid={Boolean(errors.environmentUrl)}
          aria-describedby={
            errors.environmentUrl
              ? 'd365-environment-url-err'
              : 'd365-environment-url-help'
          }
        />
        {errors.environmentUrl ? (
          <p id="d365-environment-url-err" className="text-xs text-destructive">
            {errors.environmentUrl}
          </p>
        ) : (
          <p
            id="d365-environment-url-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            The environment this workspace should use. Copy it from the browser
            address bar in Dynamics — anything after the host is ignored.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="d365-tenant-id" className="text-xs">
          Directory (tenant) ID
        </Label>
        <Input
          id="d365-tenant-id"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="00000000-0000-0000-0000-000000000000"
          value={values.tenantId}
          onChange={handleChange('tenantId')}
          disabled={submitting}
          aria-invalid={Boolean(errors.tenantId)}
          aria-describedby={errors.tenantId ? 'd365-tenant-id-err' : undefined}
        />
        {errors.tenantId && (
          <p id="d365-tenant-id-err" className="text-xs text-destructive">
            {errors.tenantId}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="d365-client-id" className="text-xs">
          Application (client) ID
        </Label>
        <Input
          id="d365-client-id"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="00000000-0000-0000-0000-000000000000"
          value={values.clientId}
          onChange={handleChange('clientId')}
          disabled={submitting}
          aria-invalid={Boolean(errors.clientId)}
          aria-describedby={
            errors.clientId ? 'd365-client-id-err' : 'd365-client-id-help'
          }
        />
        {errors.clientId ? (
          <p id="d365-client-id-err" className="text-xs text-destructive">
            {errors.clientId}
          </p>
        ) : (
          <p
            id="d365-client-id-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Both IDs are on the app registration&apos;s Overview page in Entra.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="d365-client-secret" className="text-xs">
          Client secret
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="d365-client-secret"
              type={showSecret ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="Client secret value"
              value={values.clientSecret}
              onChange={handleChange('clientSecret')}
              disabled={submitting}
              aria-invalid={Boolean(errors.clientSecret)}
              aria-describedby={
                errors.clientSecret
                  ? 'd365-client-secret-err'
                  : 'd365-client-secret-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              aria-label={showSecret ? 'Hide secret' : 'Show secret'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showSecret ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={submitting}
            aria-label="Paste secret from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.clientSecret ? (
          <p id="d365-client-secret-err" className="text-xs text-destructive">
            {errors.clientSecret}
          </p>
        ) : (
          <p
            id="d365-client-secret-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Paste the secret <span className="font-medium">Value</span>, not the
            Secret ID — Entra shows both, and the value is only visible right
            after you create it.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
